import React from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { Formik } from 'formik';
import { FaWindowRestore } from 'react-icons/fa';
import { Link, RouteComponentProps, withRouter } from 'react-router-dom';
import { Card, Col, Form, Row } from 'react-bootstrap';
import * as Yup from 'yup';

import PageTitle from 'components/PageTitle';
import Loading from 'components/Loading';
import Error from 'components/Error';
import FormHeader from 'components/FormHeader';
import FormWrapper from 'components/FormWrapper';
import FormSubtitle from 'components/FormSubtitle';
import PanelButton from 'components/PanelButton';
import BrlCurrencyInput from 'components/BrlCurrencyInput';

import swal from 'services/swal';
import hashIds from 'services/hashIds';
import history from 'services/history';
import api from 'services/api';
import toast from 'services/toast';

import useSites from 'hooks/useSites';
import useCustomer from 'hooks/useCustomer';
import useMarketingServices from 'hooks/useMarketingServices';

import { StoreState } from 'store/createStore';

interface MatchParams {
  idCustomer: string;
  idService: string;
}

type ServiceProps = RouteComponentProps<MatchParams>;

type FormValues = {
  idSite: number;
  price: string;
  installments: number;
  setup: 'PAGAMENTO' | 'DEBITO' | 'PROX_VENCIMENTO';
};

type SaleServiceResponse = {
  data: {
    info: string;
    url: string | null;
  };
};

const Service: React.FC<ServiceProps> = ({ match }) => {
  const { t } = useTranslation();

  const idCustomer = Number(hashIds.decode(match.params.idCustomer));
  const idService = Number(hashIds.decode(match.params.idService));

  const { idRepresentante } = useSelector(
    (state: StoreState) => state.client.info,
  );

  const {
    marketingServices,
    isLoading: loadingServices,
    isError: servicesError,
  } = useMarketingServices(idRepresentante);

  const {
    customer,
    isLoading: loadingCustomer,
    isError: customerError,
  } = useCustomer(idRepresentante, idCustomer);

  const { sites, loading: loadingSites, error: sitesError } = useSites(
    idRepresentante,
  );

  const service = marketingServices.find(
    marketingService => marketingService.idServico === idService,
  );

  const error = servicesError || customerError || sitesError;
  const loading = loadingServices || loadingCustomer || loadingSites;

  const customerSites = sites.filter(
    site => site.idCliente === idCustomer && site.ativo,
  );

  const serviceNotAvailableWithoutSite =
    service && service.requisito === 'SITE' && customerSites.length === 0;

  async function submitForm(values: FormValues) {
    try {
      let formattedPrice = values.price.replace(/,/, '.');
      formattedPrice = Number(formattedPrice).toFixed(2);

      let selectedSite = null;

      if (values.idSite !== 0) {
        selectedSite = sites.find(site => site.idSite === values.idSite);
      }

      const saleServiceResponse = await api.post<SaleServiceResponse>(
        'clientes/v1/representante/venda/servico',
        {
          idRepresentante,
          idServico: idService,
          idCliente: idCustomer,
          valor: formattedPrice,
          parcelas: values.installments,
          setup: values.setup,
          ...(selectedSite && { site: selectedSite.site }),
        },
      );

      const { url } = saleServiceResponse.data.data;

      if (url !== null) {
        swal.fire(
          t('pages:marketingServiceSale.successRegisteredSale'),
          t('pages:marketingServiceSale.willBeRedirected'),
          'success',
        );

        window.location.href = url;
      } else {
        toast.fire(t('pages:marketingServiceSale.saleSuccess'));

        history.push('/clientes');
      }
    } catch (err) {
      swal.fire({
        title: t('pages:marketingServiceSale.saleFailed'),
        html: err.response.data.error_description,
      });
    }
  }

  if (loading) {
    return <Loading />;
  }

  if (error) {
    return <Error />;
  }

  if (!service) {
    return <Error />;
  }

  return (
    <div>
      <PageTitle
        icon={<FaWindowRestore color="#FFFFFF" size={24} />}
        title={t('titles:customers.title')}
        description={t('titles:customers.description')}
      />

      <FormWrapper>
        <FormHeader
          title={`${t('pages:marketingServiceSale.title')} ${customer.email1}`}
          description={t('pages:marketingServiceSale.description')}
          helpContent={t('pages:marketingServiceSale.helpContent.p1')}
          startOpen
        />

        <Formik
          validateOnMount
          enableReinitialize
          initialValues={{
            idSite: customerSites.length > 0 ? customerSites[0].idSite : 0,
            installments: 1,
            price: `${service.valor.toFixed(2)}1`,
            setup: 'PAGAMENTO',
          }}
          validationSchema={Yup.object().shape({
            price: Yup.string().required(t('validations:requiredField')),
            installments: Yup.number().required(t('validations:requiredField')),
          })}
          onSubmit={submitForm}
        >
          {props => (
            <Form onSubmit={props.handleSubmit}>
              <Card.Body className="fieldset">
                <FormSubtitle
                  subTitle={t('pages:marketingServiceSale.formSubtitle')}
                />

                <Form.Group as={Row}>
                  <Form.Label column sm={2}>
                    {t('common:customer')}
                  </Form.Label>
                  <Col sm={10}>
                    <Form.Control
                      id="customer"
                      value={customer.email1}
                      plaintext
                      readOnly
                    />
                  </Col>
                </Form.Group>

                <Form.Group as={Row}>
                  <Form.Label column sm={2}>
                    {t('common:service')}
                  </Form.Label>
                  <Col sm={10}>
                    <Form.Control
                      id="serviceName"
                      value={service.servico}
                      plaintext
                      readOnly
                    />
                  </Col>
                </Form.Group>

                {customerSites.length > 0 && (
                  <Form.Group as={Row}>
                    <Form.Label column sm={2}>
                      {t('common:site')}
                    </Form.Label>
                    <Col sm={10}>
                      <Form.Control
                        as="select"
                        name="idSite"
                        onChange={e =>
                          props.setFieldValue('idSite', Number(e.target.value))
                        }
                      >
                        {customerSites.map(site => (
                          <option
                            key={`customer-site-${site.idSite}`}
                            value={site.idSite}
                          >
                            {site.site}
                          </option>
                        ))}
                      </Form.Control>
                    </Col>
                  </Form.Group>
                )}

                <Form.Group as={Row}>
                  <Form.Label column sm={2}>
                    {t('common:price')}
                  </Form.Label>
                  <Col sm={10}>
                    <BrlCurrencyInput
                      onValueChange={value =>
                        props.setFieldValue('price', value)
                      }
                      value={props.values.price}
                    />

                    <Form.Text className="text-muted">
                      {t('pages:marketingServiceSale.priceTip')}
                    </Form.Text>

                    {props.errors.price && (
                      <Form.Control.Feedback type="invalid" className="d-block">
                        {props.errors.price}
                      </Form.Control.Feedback>
                    )}
                  </Col>
                </Form.Group>
              </Card.Body>

              {serviceNotAvailableWithoutSite ? (
                <Form.Group as={Row}>
                  <Form.Label column sm={2} />
                  <Col sm={10}>
                    <div className="mb-4">
                      Esse serviço requer a contratação de uma hospedagem de
                      site.
                    </div>
                    <PanelButton
                      variant="secondary"
                      forwardedAs={Link}
                      to={`/automacao-marketing/servicos/${match.params.idService}/venda`}
                      disabled={props.isSubmitting}
                    >
                      Escolher outro serviço
                    </PanelButton>
                  </Col>
                </Form.Group>
              ) : (
                <div className="border-top pt-2 pb-2 pl-3">
                  <PanelButton
                    className="mr-1"
                    disabled={props.isSubmitting || !props.isValid}
                    onClick={() => {
                      props.setFieldValue('setup', 'PAGAMENTO');
                      props.submitForm();
                    }}
                  >
                    {props.isSubmitting
                      ? t('common:sending')
                      : t('pages:marketingServiceSale.buttons.chargeNow')}
                  </PanelButton>

                  <PanelButton
                    variant="secondary"
                    forwardedAs={Link}
                    to={`/automacao-marketing/servicos/${match.params.idService}/venda`}
                    disabled={props.isSubmitting}
                  >
                    {t('common:back')}
                  </PanelButton>
                </div>
              )}
            </Form>
          )}
        </Formik>
      </FormWrapper>
    </div>
  );
};

export default withRouter(Service);
